import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import NetworkAdmin from '../components/admin/NetworkAdmin';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DIRECTORY_CATEGORIES = [
  'Nonprofit / NGO',
  'Research Lab / Academic',
  'Government / Regulatory',
  'Industry / Company',
  'Tool / Technology Provider',
  'Advocacy / Community Group',
];

const DIRECTORY_STATUS_ORDER = [
  'Submitted',
  'Under Review',
  'Needs More Information',
  'Approved',
  'Rejected',
  'Archived',
];

// Safely extract a human-readable error string. FastAPI 422 returns `detail`
// as an array of objects — passing that to toast.error() (a React child) would
// crash the app (white screen). Always coerce to a string here.
const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};

const AdminPage = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [tab, setTab] = useState('demo');
  const [requests, setRequests] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [whitepaperRequests, setWhitepaperRequests] = useState([]);
  const [councilApplications, setCouncilApplications] = useState([]);
  const [reviewApp, setReviewApp] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [exportStats, setExportStats] = useState(null);
  const [exportFilters, setExportFilters] = useState({});
  const [downloading, setDownloading] = useState(null);
  const [approvedLink, setApprovedLink] = useState(null);
  const [directoryListings, setDirectoryListings] = useState([]);
  const [dirStatuses, setDirStatuses] = useState([]);
  const [reviewListing, setReviewListing] = useState(null);
  const [listingForm, setListingForm] = useState(null);
  const [savingListing, setSavingListing] = useState(false);
  const [listingLogoUploading, setListingLogoUploading] = useState(false);
  const [advisoryEnquiries, setAdvisoryEnquiries] = useState([]);
  const [vrpApplications, setVrpApplications] = useState([]);
  const [vrpStatuses, setVrpStatuses] = useState([]);
  const [vrpView, setVrpView] = useState('list');
  const [reviewVrp, setReviewVrp] = useState(null);
  const [vrpForm, setVrpForm] = useState(null);
  const [savingVrp, setSavingVrp] = useState(false);
  const [googleAdmin, setGoogleAdmin] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showRecovery, setShowRecovery] = useState(false);

  // An individual @safestandard.ai Google identity is the normal way in — that is
  // what makes the audit trail meaningful. The shared key stays as break-glass.
  useEffect(() => {
    const check = async () => {
      try {
        const me = await axios.get(`${API}/network/auth/me`, { withCredentials: true });
        const key = await axios.get(`${API}/network/auth/legacy-key`, { withCredentials: true });
        setGoogleAdmin(me.data);
        setPassword(key.data.key);
        setAuthenticated(true);
      } catch (e) {
        // no active session — show the sign-in screen
      } finally {
        setCheckingSession(false);
      }
    };
    check();
  }, []);

  useEffect(() => {
    if (!authenticated || !password) return;
    fetchRequests();
    fetchSignatories();
    fetchWhitepaperRequests();
    fetchCouncilApplications();
    fetchDirectoryListings();
    fetchExportStats();
    fetchAdvisoryEnquiries();
    fetchVrpApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, password]);

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/network/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* clearing local state regardless */ }
    setGoogleAdmin(null);
    setAuthenticated(false);
    setPassword('');
    window.location.href = '/admin';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setAuthenticated(true);
      }
    } catch (error) {
      toast.error('Invalid password');
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/demo-requests?password=${password}`);
      setRequests(response.data.requests);
    } catch (error) { console.error('Failed to load demo requests:', error); }
  };

  const fetchSignatories = async () => {
    try {
      const response = await axios.get(`${API}/admin/signatories?password=${password}`);
      setSignatories(response.data.signatories);
    } catch (error) { console.error('Failed to load signatories:', error); }
  };

  const fetchWhitepaperRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/whitepaper-requests?password=${password}`);
      setWhitepaperRequests(response.data.requests);
    } catch (error) { console.error('Failed to load whitepaper requests:', error); }
  };

  const handleApproveWhitepaper = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/whitepaper/${id}/approve?password=${password}`);
      if (res.data.success) {
        const link = `${window.location.origin}/api/whitepaper/download/${res.data.token}`;
        try {
          await navigator.clipboard.writeText(link);
          toast.success('Approved — download link copied to clipboard', { description: link });
        } catch {
          toast.success('Approved — copy the link from the screen', { description: link });
        }
        setApprovedLink({ id, email: res.data.email, link });
        fetchWhitepaperRequests();
      }
    } catch (error) { toast.error('Failed'); }
  };

  const handleRevokeWhitepaper = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/whitepaper/${id}/revoke?password=${password}`);
      if (res.data.success) { toast.success('Whitepaper access revoked'); fetchWhitepaperRequests(); }
    } catch (error) { toast.error('Failed'); }
  };

  const fetchCouncilApplications = async () => {
    try {
      const res = await axios.get(`${API}/admin/council-applications?password=${password}`);
      setCouncilApplications(res.data.applications || []);
    } catch (error) { console.error('Failed to load council applications:', error); }
  };

  const handleApproveApplication = async (id, tier) => {
    if (!tier) { toast.error('Select a destination tier first.'); return; }
    try {
      const res = await axios.post(`${API}/admin/council-applications/${id}/approve?password=${password}&tier=${tier}`);
      if (res.data.success) {
        toast.success('Application approved — now live on /council');
        setReviewApp(null);
        fetchCouncilApplications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleRejectApplication = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/council-applications/${id}/reject?password=${password}`);
      if (res.data.success) {
        toast.success('Application rejected');
        setReviewApp(null);
        fetchCouncilApplications();
      }
    } catch (error) { toast.error('Failed'); }
  };

  const handleRepend = async (id) => {
    if (!window.confirm('Move this record back to Pending review? An approved member is unpublished from the public Council page.')) return;
    try {
      const res = await axios.post(`${API}/admin/council-applications/${id}/pending?password=${password}`);
      if (res.data.success) {
        toast.success('Moved back to pending review');
        setReviewApp(null);
        fetchCouncilApplications();
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleReorder = async (id, direction) => {
    try {
      const res = await axios.post(`${API}/admin/council-applications/${id}/reorder?direction=${direction}&password=${password}`);
      if (res.data?.success) fetchCouncilApplications();
    } catch (error) { toast.error('Reorder failed'); }
  };

  const openEdit = (app) => {
    setEditForm({
      full_name: app.full_name || '',
      email: app.email || '',
      phone: app.phone || '',
      mailing_address: app.mailing_address || '',
      country: app.country || '',
      display_name: app.display_name || '',
      title: app.title || '',
      location_display: app.location_display || '',
      organization: app.organization || '',
      photo_url: app.photo_url || '',
      linkedin: app.linkedin || '',
      website: app.website || '',
      public_email: app.public_email || '',
      short_bio: app.short_bio || '',
      long_bio: app.long_bio || '',
      why_joined: app.why_joined || '',
      expertise_areas: app.expertise_areas || '',
      tier: app.tier || '',
    });
    setEditMode(true);
  };

  const closeEdit = () => {
    setEditMode(false);
    setEditForm(null);
  };

  const handleEditField = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Photo must be under 8 MB.'); return; }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Photo must be JPEG, PNG, or WebP.'); return;
    }
    setEditPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        const absoluteUrl = res.data.url.startsWith('http')
          ? res.data.url
          : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        setEditForm((prev) => ({ ...prev, photo_url: absoluteUrl }));
        toast.success('Photo uploaded — click Save changes to publish.');
      } else { toast.error('Upload failed.'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally { setEditPhotoUploading(false); }
  };

  const handleSaveEdit = async () => {
    if (!reviewApp || !editForm) return;
    setSavingEdit(true);
    try {
      // Strip empty-string tier so backend doesn't reject it
      const payload = { ...editForm };
      if (!payload.tier) delete payload.tier;
      const res = await axios.put(
        `${API}/admin/council-applications/${reviewApp.id}?password=${password}`,
        payload,
      );
      if (res.data?.success) {
        toast.success('Changes saved — live on /council');
        setReviewApp(res.data.application || { ...reviewApp, ...payload });
        setEditMode(false);
        setEditForm(null);
        fetchCouncilApplications();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingEdit(false); }
  };

  const fetchDirectoryListings = async () => {
    try {
      const res = await axios.get(`${API}/admin/directory-listings?password=${password}`);
      setDirectoryListings(res.data.listings || []);
      setDirStatuses(res.data.statuses || []);
    } catch (error) { console.error('Failed to load directory listings:', error); }
  };

  const fetchAdvisoryEnquiries = async () => {
    try {
      const res = await axios.get(`${API}/admin/advisory-enquiries?password=${password}`);
      setAdvisoryEnquiries(res.data.enquiries || []);
    } catch (error) { console.error('Failed to load advisory enquiries:', error); }
  };

  const handleAdvisoryStatus = async (id, status) => {
    try {
      const res = await axios.put(`${API}/admin/advisory-enquiries/${id}/status?password=${password}&status=${encodeURIComponent(status)}`);
      if (res.data.success) {
        toast.success(`Status updated to "${status}"`);
        setAdvisoryEnquiries((prev) => prev.map((e) => e.id === id ? { ...e, advisory_status: status } : e));
      }
    } catch (err) {
      toast.error(errMsg(err, 'Failed to update status'));
    }
  };

  const fetchVrpApplications = async () => {
    try {
      const res = await axios.get(`${API}/admin/verification-readiness?password=${password}`);
      setVrpApplications(res.data.applications || []);
      setVrpStatuses(res.data.statuses || []);
    } catch (error) { console.error('Failed to load VRP applications:', error); }
  };

  const openVrpReview = (app) => {
    setReviewVrp(app);
    setVrpForm({
      status: app.status || 'New',
      assigned_owner: app.assigned_owner || '',
      internal_notes: app.internal_notes || '',
      tags: (app.tags || []).join(', '),
      next_action: app.next_action || '',
      next_action_date: app.next_action_date || '',
    });
  };

  const saveVrp = async () => {
    if (!reviewVrp) return;
    setSavingVrp(true);
    try {
      const payload = {
        status: vrpForm.status,
        assigned_owner: vrpForm.assigned_owner,
        internal_notes: vrpForm.internal_notes,
        tags: vrpForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
        next_action: vrpForm.next_action,
        next_action_date: vrpForm.next_action_date,
      };
      const res = await axios.put(`${API}/admin/verification-readiness/${reviewVrp.id}?password=${password}`, payload);
      if (res.data.success) {
        toast.success('Application updated');
        setVrpApplications((prev) => prev.map((a) => a.id === reviewVrp.id ? res.data.application : a));
        setReviewVrp(null);
        setVrpForm(null);
      }
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save application'));
    } finally {
      setSavingVrp(false);
    }
  };


  const openListingReview = (l) => {
    setReviewListing(l);
    setListingForm({
      org_name: l.org_name || '',
      website: l.website || '',
      description: l.description || '',
      category: l.category || DIRECTORY_CATEGORIES[0],
      region: l.region || '',
      country: l.country || '',
      logo_url: l.logo_url || '',
      contact_email: l.contact_email || '',
      submitter_name: l.submitter_name || '',
      submitter_email: l.submitter_email || '',
      listing_status: l.listing_status || 'Submitted',
      published: !!l.published,
      admin_note: l.admin_note || '',
    });
  };

  const closeListingReview = () => {
    setReviewListing(null);
    setListingForm(null);
  };

  const handleListingField = (e) => {
    const { name, value, type, checked } = e.target;
    setListingForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleListingLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Logo must be under 8 MB.'); return; }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Logo must be JPEG, PNG, or WebP.'); return;
    }
    setListingLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        const absoluteUrl = res.data.url.startsWith('http')
          ? res.data.url
          : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        setListingForm((p) => ({ ...p, logo_url: absoluteUrl }));
        toast.success('Logo uploaded — click Save to publish.');
      } else { toast.error('Upload failed.'); }
    } catch (err) {
      toast.error(errMsg(err, 'Upload failed.'));
    } finally { setListingLogoUploading(false); }
  };

  const saveListing = async (overrides = {}) => {
    if (!reviewListing || !listingForm) return;
    setSavingListing(true);
    try {
      const payload = { ...listingForm, ...overrides };
      const res = await axios.put(
        `${API}/admin/directory-listings/${reviewListing.id}?password=${password}`,
        payload,
      );
      if (res.data?.success) {
        toast.success(overrides.published ? 'Listing approved & published' : 'Listing saved');
        closeListingReview();
        fetchDirectoryListings();
      }
    } catch (err) {
      toast.error(errMsg(err, 'Save failed'));
    } finally { setSavingListing(false); }
  };

  const quickSetPublish = async (listing, publish) => {
    try {
      const payload = publish
        ? { listing_status: 'Approved', published: true }
        : { published: false };
      const res = await axios.put(
        `${API}/admin/directory-listings/${listing.id}?password=${password}`,
        payload,
      );
      if (res.data?.success) {
        toast.success(publish ? `Published — ${listing.org_name} is now live` : `Unpublished — ${listing.org_name} hidden`);
        fetchDirectoryListings();
      }
    } catch (err) {
      toast.error(errMsg(err, 'Update failed'));
    }
  };

  const deleteListing = async (id) => {
    if (!window.confirm('Permanently delete this listing? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/admin/directory-listings/${id}?password=${password}`);
      if (res.data?.success) { toast.success('Listing deleted'); closeListingReview(); fetchDirectoryListings(); }
    } catch (err) { toast.error('Delete failed'); }
  };

  const TIER_OPTIONS = [
    { value: 'council_member', label: 'Council Member' },
    { value: 'founding_member', label: 'Founding Member' },
    { value: 'advisor', label: 'Advisor' },
    { value: 'council_ambassador', label: 'Council Ambassador' },
  ];

  const handleApproveDemo = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/approve-demo/${id}?password=${password}`);
      if (res.data.success) {
        toast.success(`Approved. Token: ${res.data.access_token}`);
        fetchRequests();
      }
    } catch (error) { toast.error('Failed'); }
  };

  const handleRevokeDemo = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/revoke-demo/${id}?password=${password}`);
      if (res.data.success) { toast.success('Revoked'); fetchRequests(); }
    } catch (error) { toast.error('Failed'); }
  };

  const handleApproveSignatory = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/signatory/${id}/approve?password=${password}`);
      if (res.data.success) { toast.success('Signatory approved'); fetchSignatories(); }
    } catch (error) { toast.error('Failed'); }
  };

  const handleRejectSignatory = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/signatory/${id}/reject?password=${password}`);
      if (res.data.success) { toast.success('Signatory rejected'); fetchSignatories(); }
    } catch (error) { toast.error('Failed'); }
  };

  const fetchExportStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/export-stats?password=${password}`);
      setExportStats(res.data);
    } catch (error) { console.error('Failed to load export stats:', error); }
  };

  const handleDownloadCsv = async (collection) => {
    const filterValue = exportFilters[collection] || '';
    const key = `${collection}-${filterValue}`;
    setDownloading(key);
    try {
      const res = await axios.get(
        `${API}/admin/export/${collection}?password=${password}&filter_value=${encodeURIComponent(filterValue)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      link.href = url;
      link.download = match ? match[1] : `${collection}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setDownloading(null);
    }
  };

  const exportSections = [
    {
      key: 'form_submissions',
      title: 'Form Submissions',
      description: 'Institutional, Whitepaper, Council, Certification, SDK & Advisory enquiry forms.',
      filterLabel: 'Filter by form type',
    },
    {
      key: 'signatories',
      title: 'Open Letter Signatories',
      description: 'All Open Letter signatures with moderation status and consents.',
      filterLabel: 'Filter by status',
    },
    {
      key: 'demo_access',
      title: 'SAIL Demo Requests',
      description: 'Demo access requests and approval tokens.',
      filterLabel: 'Filter by status',
    },
    {
      key: 'supporters',
      title: 'Supporters',
      description: 'Successful supporter contributions and pending sessions (Stripe-backed).',
      filterLabel: 'Filter by status',
    },
  ];

  if (!authenticated) {
    return (
      <div data-testid="admin-page" className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-md p-10">
          <h1 className="text-lg font-bold text-slate-900 mb-2">SAFE Admin</h1>
          <p className="text-sm text-slate-600 mb-8 leading-relaxed">
            Sign in with your SAFE Standard Google account. Access is restricted to
            <span className="font-semibold"> @safestandard.ai</span> addresses, and every action is
            recorded against your name.
          </p>
          {checkingSession ? (
            <div className="text-sm text-slate-500" data-testid="admin-session-checking">Checking your session…</div>
          ) : (
            <>
              <button onClick={startGoogleLogin} className="btn-primary w-full" data-testid="admin-btn-google-login">
                Sign in with Google
              </button>
              <div className="mt-8 pt-6 border-t border-slate-200">
                {showRecovery ? (
                  <form onSubmit={handleLogin} className="space-y-3" data-testid="admin-recovery-form">
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Recovery access. Actions taken this way are logged without an individual name.
                    </p>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Recovery password"
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
                      data-testid="admin-input-password" />
                    <button type="submit" className="w-full text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors" data-testid="admin-btn-login">
                      Use recovery access
                    </button>
                  </form>
                ) : (
                  <button onClick={() => setShowRecovery(true)} data-testid="admin-show-recovery"
                    className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 transition-colors">
                    Recovery access
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start justify-between gap-6">
            <h1 className="text-2xl font-bold">Admin</h1>
            <div className="text-right">
              {googleAdmin ? (
                <>
                  <div className="text-sm font-medium text-white" data-testid="admin-identity">{googleAdmin.name || googleAdmin.email}</div>
                  <div className="text-xs text-slate-400">{googleAdmin.email} · {googleAdmin.role}</div>
                </>
              ) : (
                <div className="text-xs text-amber-400" data-testid="admin-identity">Recovery access — actions not attributed</div>
              )}
              <button onClick={handleLogout} data-testid="admin-btn-logout"
                className="text-xs text-slate-400 hover:text-white underline underline-offset-4 mt-1 transition-colors">
                Sign out
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-4">
            <button onClick={() => setTab('network')} data-testid="admin-tab-network" className={`text-sm font-medium px-4 py-2 ${tab === 'network' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Council Network
            </button>
            <button onClick={() => setTab('demo')} data-testid="admin-tab-demo" className={`text-sm font-medium px-4 py-2 ${tab === 'demo' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Demo Requests ({requests.length})
            </button>
            <button onClick={() => setTab('signatories')} data-testid="admin-tab-signatories" className={`text-sm font-medium px-4 py-2 ${tab === 'signatories' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Signatories ({signatories.length})
            </button>
            <button onClick={() => setTab('onboarding')} data-testid="admin-tab-onboarding" className={`text-sm font-medium px-4 py-2 ${tab === 'onboarding' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Council Onboarding ({councilApplications.filter(a => a.status === 'pending').length}/{councilApplications.length})
            </button>
            <button onClick={() => { setTab('directory'); fetchDirectoryListings(); }} data-testid="admin-tab-directory" className={`text-sm font-medium px-4 py-2 ${tab === 'directory' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Directory ({directoryListings.filter(l => ['Submitted', 'Under Review', 'Needs More Information'].includes(l.listing_status || 'Submitted')).length}/{directoryListings.length})
            </button>
            <button onClick={() => { setTab('advisory'); fetchAdvisoryEnquiries(); }} data-testid="admin-tab-advisory" className={`text-sm font-medium px-4 py-2 ${tab === 'advisory' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Advisory ({advisoryEnquiries.length})
            </button>
            <button onClick={() => { setTab('vrp'); fetchVrpApplications(); }} data-testid="admin-tab-vrp" className={`text-sm font-medium px-4 py-2 ${tab === 'vrp' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Verification Readiness ({vrpApplications.length})
            </button>
            <button onClick={() => { setTab('exports'); fetchExportStats(); }} data-testid="admin-tab-exports" className={`text-sm font-medium px-4 py-2 ${tab === 'exports' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              Exports
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === 'network' && (
          googleAdmin ? (
            <NetworkAdmin admin={googleAdmin} />
          ) : (
            <div className="bg-white border border-slate-200 p-8" data-testid="network-requires-google">
              <h3 className="text-sm font-bold text-slate-900 mb-2">Sign in with Google to open the Council Network</h3>
              <p className="text-sm text-slate-600 max-w-2xl leading-relaxed">
                Council Network records include private profiles, referrals and relationship
                intelligence, so they are only released to an individually authenticated
                @safestandard.ai account. Recovery access cannot open this area.
              </p>
              <button onClick={handleLogout} className="btn-primary mt-6" data-testid="network-signin-google">
                Sign in with Google
              </button>
            </div>
          )
        )}
        {tab === 'demo' && (
          <div className="space-y-4">
            {requests.map((req) => (
              <div key={req.id} className="bg-white border border-slate-200 p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-base font-semibold text-slate-900">{req.full_name}</h3>
                      <span className={`text-xs font-medium px-2 py-0.5 ${req.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : req.status === 'revoked' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {req.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{req.email}</p>
                    {req.organization && <p className="text-sm text-slate-500">{req.organization} {req.role && `— ${req.role}`}</p>}
                    {req.reason && <p className="text-sm text-slate-500 mt-1 italic">"{req.reason}"</p>}
                    <p className="text-xs text-slate-400 mt-2">Requested: {new Date(req.requested_at).toLocaleDateString()}</p>
                    {req.access_token && <p className="text-xs font-mono text-cyan-700 mt-1">Token: {req.access_token}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {req.status === 'pending' && (
                      <button onClick={() => handleApproveDemo(req.id)} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700">Approve</button>
                    )}
                    {req.status === 'approved' && (
                      <button onClick={() => handleRevokeDemo(req.id)} className="bg-red-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-700">Revoke</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {requests.length === 0 && <p className="text-slate-500 text-center py-12">No demo access requests yet.</p>}
          </div>
        )}

        {tab === 'signatories' && (
          <div className="space-y-4">
            {signatories.map((s) => (
              <div key={s.id} className="bg-white border border-slate-200 p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-base font-semibold text-slate-900">{s.first_name} {s.last_name}</h3>
                      <span className={`text-xs font-medium px-2 py-0.5 ${s.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : s.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {s.status}
                      </span>
                      {s.public_display && <span className="text-xs font-medium px-2 py-0.5 bg-cyan-50 text-cyan-700">Public</span>}
                    </div>
                    <p className="text-sm text-slate-600">{s.email}</p>
                    <p className="text-sm text-slate-500">{s.country} {s.organization && `· ${s.organization}`} {s.role && `· ${s.role}`}</p>
                    {s.industry && <p className="text-sm text-slate-500">{s.industry}</p>}
                    {s.linkedin_website && <p className="text-xs text-cyan-700 mt-1">{s.linkedin_website}</p>}
                    {s.why_signing && <p className="text-sm text-slate-500 mt-1 italic">"{s.why_signing}"</p>}
                    <p className="text-xs text-slate-400 mt-2">Signed: {new Date(s.signed_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {s.status === 'pending' && (
                      <>
                        <button onClick={() => handleApproveSignatory(s.id)} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700">Approve</button>
                        <button onClick={() => handleRejectSignatory(s.id)} className="bg-red-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-700">Reject</button>
                      </>
                    )}
                    {s.status === 'approved' && (
                      <button onClick={() => handleRejectSignatory(s.id)} className="bg-red-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-700">Remove</button>
                    )}
                    {s.status === 'rejected' && (
                      <button onClick={() => handleApproveSignatory(s.id)} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700">Approve</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {signatories.length === 0 && <p className="text-slate-500 text-center py-12">No signatories yet.</p>}
          </div>
        )}

        {tab === 'onboarding' && (
          <div className="space-y-6" data-testid="admin-onboarding-panel">
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-1">Council Onboarding</h2>
              <p className="text-sm text-slate-500">Public form link: <code className="bg-slate-100 px-2 py-0.5 text-xs">/onboarding</code>. Approved members appear on the public Council page in their assigned tier, ordered as below.</p>
            </div>

            {/* Pending applications */}
            {councilApplications.length > 0 && (
              <div className="bg-white border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Pending review</h3>
                <span className="text-xs text-slate-500">{councilApplications.filter(a => a.status === 'pending').length} pending</span>
              </div>
              <div className="divide-y divide-slate-100">
                {councilApplications.filter(a => a.status === 'pending').map((app) => (
                  <div key={app.id} className="px-6 py-4" data-testid={`application-row-${app.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        {app.photo_url && (
                          <img src={app.photo_url} alt={app.display_name || app.full_name} className="w-12 h-12 object-cover flex-shrink-0 border border-slate-200" />
                        )}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{app.display_name || app.full_name}</h4>
                          <p className="text-xs text-slate-500">{app.title} · {app.country}</p>
                        </div>
                      </div>
                      <button onClick={() => setReviewApp(app)} className="bg-slate-100 text-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-200" data-testid={`review-application-${app.id}`}>
                        Review
                      </button>
                    </div>
                  </div>
                ))}
                {councilApplications.filter(a => a.status === 'pending').length === 0 && councilApplications.length > 0 && (
                  <p className="text-slate-500 text-center py-8 text-sm">No pending applications.</p>
                )}
              </div>
              </div>
            )}

            {/* Approved members grouped by tier with reorder controls */}
            {TIER_OPTIONS.map((tier) => {
              const members = councilApplications.filter(a => a.status === 'approved' && a.tier === tier.value);
              if (members.length === 0) return null;
              return (
                <div key={tier.value} className="bg-white border border-slate-200">
                  <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">{tier.label}s</h3>
                    <span className="text-xs text-slate-500">{members.length} approved · use arrows to reorder</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {members.map((app, idx) => (
                      <div key={app.id} className="px-6 py-4 flex items-center gap-4" data-testid={`approved-row-${app.id}`}>
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button onClick={() => handleReorder(app.id, 'up')} disabled={idx === 0} className={`w-7 h-7 flex items-center justify-center border ${idx === 0 ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid={`reorder-up-${app.id}`} aria-label="Move up">▲</button>
                          <button onClick={() => handleReorder(app.id, 'down')} disabled={idx === members.length - 1} className={`w-7 h-7 flex items-center justify-center border ${idx === members.length - 1 ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid={`reorder-down-${app.id}`} aria-label="Move down">▼</button>
                        </div>
                        {app.photo_url && (
                          <img src={app.photo_url} alt={app.display_name || app.full_name} className="w-12 h-12 object-cover flex-shrink-0 border border-slate-200" />
                        )}
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-slate-900">{app.display_name || app.full_name}</h4>
                          <p className="text-xs text-slate-500">{app.title} · {app.location_display}</p>
                        </div>
                        <button onClick={() => setReviewApp(app)} className="bg-slate-100 text-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-200">
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Rejected (collapsed list) */}
            {councilApplications.filter(a => a.status === 'rejected').length > 0 && (
              <div className="bg-white border border-slate-200">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Rejected</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {councilApplications.filter(a => a.status === 'rejected').map((app) => (
                    <div key={app.id} className="px-6 py-3 flex justify-between items-center">
                      <div>
                        <span className="text-sm text-slate-700">{app.display_name || app.full_name}</span>
                        <span className="text-xs text-slate-400 ml-2">{app.email}</span>
                      </div>
                      <button onClick={() => setReviewApp(app)} className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4" data-testid={`review-rejected-${app.id}`}>Review &amp; change status</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {councilApplications.length === 0 && (
              <p className="text-slate-500 text-center py-12 text-sm">No applications yet. Share <code className="bg-slate-100 px-2 py-0.5">/onboarding</code> with prospective members.</p>
            )}
          </div>
        )}

        {reviewApp && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setReviewApp(null); closeEdit(); }} data-testid="review-modal-overlay">
            <div className="bg-white w-full max-w-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
              <div className="p-6 max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    {(editMode ? editForm?.photo_url : reviewApp.photo_url) && (
                      <img src={editMode ? editForm.photo_url : reviewApp.photo_url} alt={reviewApp.display_name || reviewApp.full_name} className="w-20 h-20 object-cover border border-slate-200" />
                    )}
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">{reviewApp.display_name || reviewApp.full_name}</h2>
                      <p className="text-sm text-cyan-700 font-medium">{reviewApp.title}</p>
                      <p className="text-xs text-slate-400 mt-1">{reviewApp.location_display}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!editMode && (
                      <button
                        onClick={() => openEdit(reviewApp)}
                        className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700"
                        data-testid="edit-application-btn"
                      >
                        Edit
                      </button>
                    )}
                    <button onClick={() => { setReviewApp(null); closeEdit(); }} className="w-8 h-8 bg-slate-100 flex items-center justify-center hover:bg-slate-200">×</button>
                  </div>
                </div>

                {!editMode && (
                  <>
                    <Section title="Internal contact">
                      <Row label="Legal name" value={reviewApp.full_name} />
                      <Row label="Email" value={reviewApp.email} />
                      <Row label="Phone" value={reviewApp.phone} />
                      <Row label="Country" value={reviewApp.country} />
                      <Row label="Mailing address" value={reviewApp.mailing_address} multiline />
                    </Section>

                    <Section title="Public profile">
                      <Row label="Organization" value={reviewApp.organization} />
                      <Row label="LinkedIn" value={reviewApp.linkedin} link />
                      <Row label="Website" value={reviewApp.website} link />
                      <Row label="Public email" value={reviewApp.public_email} />
                      <Row label="Short bio" value={reviewApp.short_bio} multiline />
                      <Row label="Long bio" value={reviewApp.long_bio} multiline />
                      <Row label="Why joined" value={reviewApp.why_joined} multiline />
                      <Row label="Expertise" value={reviewApp.expertise_areas} />
                    </Section>
                  </>
                )}

                {editMode && editForm && (
                  <div className="space-y-6" data-testid="edit-application-form">
                    <Section title="Profile photo">
                      <div className="flex items-center gap-4">
                        {editForm.photo_url ? (
                          <img src={editForm.photo_url} alt="Current" className="w-24 h-24 object-cover border border-slate-200" />
                        ) : (
                          <div className="w-24 h-24 border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">No photo</div>
                        )}
                        <label className={`inline-block cursor-pointer px-4 py-2 text-xs font-bold uppercase tracking-widest ${editPhotoUploading ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`}>
                          {editPhotoUploading ? 'Uploading…' : (editForm.photo_url ? 'Replace photo' : 'Upload photo')}
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleEditPhoto} disabled={editPhotoUploading} data-testid="edit-photo-input" />
                        </label>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">JPEG, PNG or WebP. Max 8 MB. Stored persistently in MongoDB.</p>
                    </Section>

                    <Section title="Internal contact">
                      <EditField label="Legal name" name="full_name" value={editForm.full_name} onChange={handleEditField} />
                      <EditField label="Email" name="email" type="email" value={editForm.email} onChange={handleEditField} />
                      <EditField label="Phone" name="phone" value={editForm.phone} onChange={handleEditField} />
                      <EditField label="Country" name="country" value={editForm.country} onChange={handleEditField} />
                      <EditField label="Mailing address" name="mailing_address" value={editForm.mailing_address} onChange={handleEditField} multiline />
                    </Section>

                    <Section title="Public profile">
                      <EditField label="Display name" name="display_name" value={editForm.display_name} onChange={handleEditField} />
                      <EditField label="Title" name="title" value={editForm.title} onChange={handleEditField} />
                      <EditField label="Location (public)" name="location_display" value={editForm.location_display} onChange={handleEditField} />
                      <EditField label="Organization" name="organization" value={editForm.organization} onChange={handleEditField} />
                      <EditField label="LinkedIn URL" name="linkedin" value={editForm.linkedin} onChange={handleEditField} />
                      <EditField label="Website" name="website" value={editForm.website} onChange={handleEditField} />
                      <EditField label="Public email" name="public_email" value={editForm.public_email} onChange={handleEditField} />
                      <EditField label="Short bio" name="short_bio" value={editForm.short_bio} onChange={handleEditField} multiline />
                      <EditField label="Long bio" name="long_bio" value={editForm.long_bio} onChange={handleEditField} multiline />
                      <EditField label="Why joined" name="why_joined" value={editForm.why_joined} onChange={handleEditField} multiline />
                      <EditField label="Expertise areas" name="expertise_areas" value={editForm.expertise_areas} onChange={handleEditField} />
                    </Section>

                    {reviewApp.status === 'approved' && (
                      <Section title="Tier (controls Council page placement)">
                        <div>
                          <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">Tier</label>
                          <select
                            name="tier"
                            value={editForm.tier}
                            onChange={handleEditField}
                            className="w-full px-3 py-2 border border-slate-300 text-sm bg-white"
                            data-testid="edit-tier-select"
                          >
                            <option value="" disabled>Choose tier…</option>
                            {TIER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <p className="text-xs text-slate-400 mt-2">Changing tier moves them to the bottom of the new group.</p>
                        </div>
                      </Section>
                    )}

                    <div className="bg-slate-50 border-t border-slate-200 -mx-6 -mb-6 px-6 py-5 mt-6 sticky bottom-0 flex justify-end gap-3">
                      <button
                        onClick={closeEdit}
                        disabled={savingEdit}
                        className="bg-slate-100 text-slate-900 px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-200 disabled:opacity-50"
                        data-testid="cancel-edit-btn"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={savingEdit || editPhotoUploading}
                        className="bg-emerald-700 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-800 disabled:bg-slate-300"
                        data-testid="save-edit-btn"
                      >
                        {savingEdit ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>
                )}

                {!editMode && (
                  <div className="bg-slate-50 border-t border-slate-200 -mx-6 -mb-6 px-6 py-5 mt-6 sticky bottom-0" data-testid="application-decision-block">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 uppercase tracking-wider ${reviewApp.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : reviewApp.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`} data-testid="application-current-status">
                        {reviewApp.status}{reviewApp.status === 'approved' && reviewApp.tier ? ` · ${(TIER_OPTIONS.find(t => t.value === reviewApp.tier) || {}).label || reviewApp.tier}` : ''}
                      </span>
                    </div>
                    {!reviewApp.email && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mb-3" data-testid="application-email-missing">
                        No email address on file. Add one via Edit — email is how their Partnership Hub submissions are matched to them.
                      </p>
                    )}
                    {reviewApp.status !== 'approved' && (
                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <select id="tier-select" defaultValue="" className="px-3 py-2 border border-slate-300 text-sm bg-white flex-1" data-testid="tier-select">
                          <option value="" disabled>Choose destination tier…</option>
                          {TIER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <button
                          onClick={() => {
                            const sel = document.getElementById('tier-select');
                            handleApproveApplication(reviewApp.id, sel.value);
                          }}
                          className="bg-emerald-700 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-800"
                          data-testid="approve-application-btn"
                        >
                          {reviewApp.status === 'rejected' ? 'Approve & publish (undo rejection)' : 'Approve & publish'}
                        </button>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {reviewApp.status !== 'rejected' && (
                        <button
                          onClick={() => handleRejectApplication(reviewApp.id)}
                          className="bg-red-600 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-700"
                          data-testid="reject-application-btn"
                        >
                          {reviewApp.status === 'approved' ? 'Reject & unpublish' : 'Reject'}
                        </button>
                      )}
                      {reviewApp.status !== 'pending' && (
                        <button
                          onClick={() => handleRepend(reviewApp.id)}
                          className="bg-white border border-slate-300 text-slate-900 px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-100"
                          data-testid="repend-application-btn"
                        >
                          Move back to pending
                        </button>
                      )}
                    </div>
                    {reviewApp.reviewed_note && (
                      <p className="text-xs text-slate-500 mt-3">Note: {reviewApp.reviewed_note}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'whitepaper' && (
          <div className="space-y-4" data-testid="admin-whitepaper-panel">
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-1">Whitepaper Requests</h2>
              <p className="text-sm text-slate-500">Approving a request generates a unique download link and copies it to your clipboard. Paste it into your email tool to send to the requester.</p>
            </div>
            {approvedLink && (
              <div className="bg-emerald-50 border border-emerald-200 p-6" data-testid="approved-link-box">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">Most recent approval — {approvedLink.email}</p>
                <p className="text-xs text-emerald-900 font-mono break-all mb-3">{approvedLink.link}</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(approvedLink.link); toast.success('Copied'); }}
                  className="text-xs font-bold uppercase tracking-widest bg-slate-900 text-white px-4 py-2 hover:bg-slate-700"
                  data-testid="copy-link-btn"
                >
                  Copy link
                </button>
              </div>
            )}
            {whitepaperRequests.map((req) => {
              const status = req.whitepaper_status || 'pending';
              return (
              <div key={req.id} className="bg-white border border-slate-200 p-6" data-testid={`whitepaper-row-${req.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-base font-semibold text-slate-900">{req.full_name}</h3>
                      <span className={`text-xs font-medium px-2 py-0.5 ${status === 'approved' ? 'bg-emerald-50 text-emerald-700' : status === 'revoked' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {status}
                      </span>
                      {req.whitepaper_downloaded_at && (
                        <span className="text-xs font-medium px-2 py-0.5 bg-cyan-50 text-cyan-700">Downloaded</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{req.email}</p>
                    {req.organization && <p className="text-sm text-slate-500">{req.organization}{req.role && ` — ${req.role}`}</p>}
                    <p className="text-xs text-slate-400 mt-2">
                      Requested: {new Date(req.submitted_at).toLocaleDateString()}
                      {req.whitepaper_approved_at && ` · Approved: ${new Date(req.whitepaper_approved_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {status === 'pending' && (
                      <button onClick={() => handleApproveWhitepaper(req.id)} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700" data-testid={`approve-whitepaper-${req.id}`}>
                        Approve & copy link
                      </button>
                    )}
                    {status === 'approved' && (
                      <>
                        <button
                          onClick={() => {
                            const link = `${window.location.origin}/api/whitepaper/download/${req.whitepaper_token}`;
                            navigator.clipboard.writeText(link);
                            toast.success('Link copied', { description: link });
                          }}
                          className="bg-slate-100 text-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-200"
                        >
                          Copy link
                        </button>
                        <button onClick={() => handleRevokeWhitepaper(req.id)} className="bg-red-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-700">
                          Revoke
                        </button>
                      </>
                    )}
                    {status === 'revoked' && (
                      <button onClick={() => handleApproveWhitepaper(req.id)} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700">
                        Re-approve
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
            {whitepaperRequests.length === 0 && <p className="text-slate-500 text-center py-12">No whitepaper requests yet.</p>}
          </div>
        )}

        {tab === 'advisory' && (
          <div className="space-y-4" data-testid="admin-advisory-panel">
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-1">Strategic Advisory Enquiries</h2>
              <p className="text-sm text-slate-500">Enquiries from the Strategic Advisory & AI Assurance page. Full submissions are also available as CSV in the Exports tab (filter form type = advisory_enquiry).</p>
            </div>
            {advisoryEnquiries.map((e) => {
              const advStatus = e.advisory_status || 'New';
              const statusColor = advStatus === 'Proposal Sent' ? 'bg-emerald-50 text-emerald-700' : advStatus === 'Reviewing' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-700';
              return (
              <div key={e.id} className="bg-white border border-slate-200 p-6" data-testid={`advisory-row-${e.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-base font-semibold text-slate-900">{e.org_name}</h3>
                      <span className={`text-xs font-semibold px-2 py-0.5 ${statusColor}`} data-testid={`advisory-status-tag-${e.id}`}>{advStatus}</span>
                      {e.current_stage && <span className="text-xs font-medium px-2 py-0.5 bg-cyan-50 text-cyan-700">{e.current_stage}</span>}
                      {e.budget && <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-600">{e.budget}</span>}
                      {e.timeframe && <span className="text-xs font-medium px-2 py-0.5 bg-amber-50 text-amber-700">{e.timeframe}</span>}
                    </div>
                    <p className="text-sm text-slate-600">{e.primary_contact} · {e.email}{e.phone ? ` · ${e.phone}` : ''}</p>
                    <p className="text-sm text-slate-500">{[e.industry, e.country, e.num_employees && `${e.num_employees} employees`].filter(Boolean).join(' · ')}</p>
                    {e.website && <p className="text-sm text-slate-500 mt-1"><a href={e.website} target="_blank" rel="noreferrer" className="text-cyan-700 underline">{e.website}</a></p>}
                    <div className="mt-3 space-y-2 text-sm text-slate-700">
                      {e.ai_project && <p><span className="font-semibold text-slate-900">AI project:</span> {e.ai_project}</p>}
                      {e.challenge && <p><span className="font-semibold text-slate-900">Challenge:</span> {e.challenge}</p>}
                      {e.success_definition && <p><span className="font-semibold text-slate-900">Success:</span> {e.success_definition}</p>}
                      {e.desired_outcomes?.length > 0 && <p><span className="font-semibold text-slate-900">Outcomes:</span> {e.desired_outcomes.join(', ')}</p>}
                      {e.additional_info && <p><span className="font-semibold text-slate-900">Additional:</span> {e.additional_info}</p>}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">Submitted: {new Date(e.submitted_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex-shrink-0 md:w-44">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Pipeline status</p>
                    <div className="flex md:flex-col gap-2">
                      {['New', 'Reviewing', 'Proposal Sent'].map((s) => (
                        <button
                          key={s}
                          onClick={() => handleAdvisoryStatus(e.id, s)}
                          disabled={advStatus === s}
                          className={`text-xs font-bold uppercase tracking-widest px-3 py-2 text-left transition-colors ${advStatus === s ? 'bg-slate-900 text-white cursor-default' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                          data-testid={`advisory-set-status-${e.id}-${s.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            {advisoryEnquiries.length === 0 && <p className="text-slate-500 text-center py-12">No advisory enquiries yet.</p>}
          </div>
        )}

        {tab === 'vrp' && (
          <div className="space-y-4" data-testid="admin-vrp-panel">
            <div className="bg-white border border-slate-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900 mb-1">Verification Readiness Applications</h2>
                <p className="text-sm text-slate-500">Applications from the SAFE Labs Verification Readiness Programme page. Full data is also available as CSV in the Exports tab.</p>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 p-1 flex-shrink-0">
                <button onClick={() => setVrpView('list')} className={`text-xs font-semibold px-4 py-2 ${vrpView === 'list' ? 'bg-white text-slate-900' : 'text-slate-500'}`} data-testid="vrp-view-list">List</button>
                <button onClick={() => setVrpView('card')} className={`text-xs font-semibold px-4 py-2 ${vrpView === 'card' ? 'bg-white text-slate-900' : 'text-slate-500'}`} data-testid="vrp-view-card">Cards</button>
              </div>
            </div>

            {vrpView === 'list' && (
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Reference</th>
                      <th className="px-4 py-3">Organisation</th>
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3">Country</th>
                      <th className="px-4 py-3">Tier</th>
                      <th className="px-4 py-3">Budget</th>
                      <th className="px-4 py-3">Timing</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Owner</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {vrpApplications.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-50" data-testid={`vrp-row-${a.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-slate-900">{a.application_reference}</td>
                        <td className="px-4 py-3 text-slate-900">{a.legal_org_name || a.trading_name || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{`${a.first_name || ''} ${a.last_name || ''}`.trim()}<br /><span className="text-xs text-slate-400">{a.work_email}</span></td>
                        <td className="px-4 py-3 text-slate-600">{a.hq_country || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{a.selected_tier || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{a.indicative_budget || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{a.desired_start_date || '—'}</td>
                        <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 ${a.completion_status === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{a.status || 'New'}</span>{a.completion_status === 'partial' && <div className="text-xs text-amber-700 mt-1" data-testid={`vrp-partial-step-${a.id}`}>Reached step {a.last_step_completed || 1} of 5</div>}</td>
                        <td className="px-4 py-3 text-slate-600">{a.assigned_owner || '—'}</td>
                        <td className="px-4 py-3 text-right"><button onClick={() => openVrpReview(a)} className="text-xs font-bold uppercase tracking-widest text-cyan-700 hover:text-cyan-900" data-testid={`vrp-review-${a.id}`}>Review</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {vrpApplications.length === 0 && <p className="text-slate-500 text-center py-12">No applications yet.</p>}
              </div>
            )}

            {vrpView === 'card' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vrpApplications.map((a) => (
                  <div key={a.id} className="bg-white border border-slate-200 p-5" data-testid={`vrp-card-${a.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs text-slate-400">{a.application_reference}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 ${a.completion_status === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{a.status || 'New'}</span>
                    </div>
                    {a.completion_status === 'partial' && (
                      <p className="text-xs text-amber-700 mb-2" data-testid={`vrp-card-partial-${a.id}`}>Unfinished — reached step {a.last_step_completed || 1} of 5</p>
                    )}
                    <h3 className="text-base font-semibold text-slate-900">{a.legal_org_name || a.trading_name || '—'}</h3>
                    <p className="text-sm text-slate-600">{`${a.first_name || ''} ${a.last_name || ''}`.trim()} · {a.job_title}</p>
                    <p className="text-xs text-slate-400 mb-3">{a.work_email}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {a.selected_tier && <span className="text-xs px-2 py-0.5 bg-cyan-50 text-cyan-700">{a.selected_tier}</span>}
                      {a.hq_country && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600">{a.hq_country}</span>}
                      {a.indicative_budget && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600">{a.indicative_budget}</span>}
                    </div>
                    <button onClick={() => openVrpReview(a)} className="text-xs font-bold uppercase tracking-widest text-cyan-700 hover:text-cyan-900" data-testid={`vrp-card-review-${a.id}`}>Review →</button>
                  </div>
                ))}
                {vrpApplications.length === 0 && <p className="text-slate-500 text-center py-12 col-span-full">No applications yet.</p>}
              </div>
            )}
          </div>
        )}


        {tab === 'exports' && (
          <div className="space-y-4" data-testid="admin-exports-panel">
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-1">Download CSV exports</h2>
              <p className="text-sm text-slate-500">Each export pulls live data directly from the database. Files open in Excel, Google Sheets, or Numbers.</p>
            </div>
            {exportSections.map((section) => {
              const stat = exportStats?.[section.key];
              const currentFilter = exportFilters[section.key] || '';
              const isDownloading = downloading === `${section.key}-${currentFilter}`;
              return (
                <div key={section.key} className="bg-white border border-slate-200 p-6" data-testid={`export-section-${section.key}`}>
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 mb-1">
                        <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                        <span className="text-xs text-slate-400">{stat ? `${stat.total} records` : '…'}</span>
                      </div>
                      <p className="text-sm text-slate-500 mb-3">{section.description}</p>
                      {stat && stat.filter_values.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider mb-2">{section.filterLabel}</label>
                          <select
                            value={currentFilter}
                            onChange={(e) => setExportFilters({ ...exportFilters, [section.key]: e.target.value })}
                            className="w-full md:w-72 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 bg-white"
                            data-testid={`export-filter-${section.key}`}
                          >
                            <option value="">All ({stat.total})</option>
                            {stat.filter_values.map((v) => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDownloadCsv(section.key)}
                      disabled={isDownloading || !stat || stat.total === 0}
                      className="bg-slate-900 text-white px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex-shrink-0"
                      data-testid={`export-download-${section.key}`}
                    >
                      {isDownloading ? 'Preparing…' : 'Download CSV'}
                    </button>
                  </div>
                </div>
              );
            })}
            {!exportStats && (
              <p className="text-slate-500 text-center py-12">Loading export stats…</p>
            )}
          </div>
        )}

        {tab === 'directory' && (
          <div className="space-y-6" data-testid="admin-directory-panel">
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-1">Directory Submissions</h2>
              <p className="text-sm text-slate-500">Public form link: <code className="bg-slate-100 px-2 py-0.5 text-xs">/directory/submit</code>. A listing appears publicly only when its status is <strong>Approved</strong> and it is <strong>Published</strong>.</p>
            </div>

            {DIRECTORY_STATUS_ORDER.map((status) => {
              const rows = directoryListings.filter(l => (l.listing_status || 'Submitted') === status);
              if (rows.length === 0) return null;
              return (
                <div key={status} className="bg-white border border-slate-200">
                  <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">{status}</h3>
                    <span className="text-xs text-slate-500">{rows.length}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {rows.map((l) => (
                      <div key={l.id} className="px-6 py-4 flex items-center gap-4" data-testid={`directory-row-${l.id}`}>
                        <div className="w-10 h-10 bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {l.logo_url ? <img src={l.logo_url} alt={l.org_name} className="w-full h-full object-contain" /> : <span className="text-xs text-slate-400">—</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-slate-900 truncate">{l.org_name}</h4>
                            {l.published && <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5">Published</span>}
                          </div>
                          <p className="text-xs text-slate-500">{l.category}{l.country ? ` · ${l.country}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {l.published ? (
                            <button onClick={() => quickSetPublish(l, false)} className="bg-white border border-amber-300 text-amber-700 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-amber-50" data-testid={`unpublish-listing-${l.id}`}>Unpublish</button>
                          ) : (
                            <button onClick={() => quickSetPublish(l, true)} className="bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-800" data-testid={`publish-listing-${l.id}`}>Approve &amp; Publish</button>
                          )}
                          <button onClick={() => openListingReview(l)} className="bg-slate-100 text-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-200" data-testid={`review-listing-${l.id}`}>Review</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {directoryListings.length === 0 && (
              <p className="text-slate-500 text-center py-12 text-sm">No directory submissions yet. Share <code className="bg-slate-100 px-2 py-0.5">/directory/submit</code>.</p>
            )}
          </div>
        )}

        {reviewListing && listingForm && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={closeListingReview} data-testid="listing-modal-overlay">
            <div className="bg-white w-full max-w-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="listing-modal">
              <div className="p-6 max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{reviewListing.org_name}</h2>
                    <p className="text-xs text-slate-400 mt-1">Submitted {reviewListing.submitted_at ? new Date(reviewListing.submitted_at).toLocaleDateString() : ''}</p>
                  </div>
                  <button onClick={closeListingReview} className="w-8 h-8 bg-slate-100 flex items-center justify-center hover:bg-slate-200">×</button>
                </div>

                <div className="space-y-6" data-testid="listing-edit-form">
                  <Section title="Workflow">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">Status</label>
                        <select name="listing_status" value={listingForm.listing_status} onChange={handleListingField} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="listing-status-select">
                          {(dirStatuses.length ? dirStatuses : DIRECTORY_STATUS_ORDER).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input type="checkbox" name="published" checked={listingForm.published} onChange={handleListingField} data-testid="listing-published-checkbox" />
                          Published (visible publicly)
                        </label>
                      </div>
                    </div>
                    <EditField label="Admin note (internal)" name="admin_note" value={listingForm.admin_note} onChange={handleListingField} multiline />
                  </Section>

                  <Section title="Logo">
                    <div className="flex items-center gap-4">
                      {listingForm.logo_url ? (
                        <img src={listingForm.logo_url} alt="logo" className="w-20 h-20 object-contain border border-slate-200" />
                      ) : (
                        <div className="w-20 h-20 border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">No logo</div>
                      )}
                      <label className={`inline-block cursor-pointer px-4 py-2 text-xs font-bold uppercase tracking-widest ${listingLogoUploading ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`}>
                        {listingLogoUploading ? 'Uploading…' : (listingForm.logo_url ? 'Replace logo' : 'Upload logo')}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleListingLogo} disabled={listingLogoUploading} data-testid="listing-logo-input" />
                      </label>
                    </div>
                  </Section>

                  <Section title="Public listing">
                    <EditField label="Organization / project name" name="org_name" value={listingForm.org_name} onChange={handleListingField} />
                    <EditField label="Website" name="website" value={listingForm.website} onChange={handleListingField} />
                    <div className="text-sm">
                      <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">Category</label>
                      <select name="category" value={listingForm.category} onChange={handleListingField} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="listing-category-select">
                        {DIRECTORY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <EditField label="Description" name="description" value={listingForm.description} onChange={handleListingField} multiline />
                    <EditField label="Region" name="region" value={listingForm.region} onChange={handleListingField} />
                    <EditField label="Country" name="country" value={listingForm.country} onChange={handleListingField} />
                  </Section>

                  <Section title="Internal (submitter)">
                    <EditField label="Submitter name" name="submitter_name" value={listingForm.submitter_name} onChange={handleListingField} />
                    <EditField label="Submitter email" name="submitter_email" value={listingForm.submitter_email} onChange={handleListingField} />
                    <EditField label="Organization contact email" name="contact_email" value={listingForm.contact_email} onChange={handleListingField} />
                  </Section>

                  <div className="bg-slate-50 border-t border-slate-200 -mx-6 -mb-6 px-6 py-5 mt-6 sticky bottom-0 flex flex-wrap justify-end gap-3">
                    <button onClick={() => deleteListing(reviewListing.id)} disabled={savingListing} className="bg-white border border-red-300 text-red-700 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-50 disabled:opacity-50 mr-auto" data-testid="delete-listing-btn">Delete</button>
                    <button onClick={closeListingReview} disabled={savingListing} className="bg-slate-100 text-slate-900 px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-200 disabled:opacity-50">Cancel</button>
                    <button onClick={() => saveListing()} disabled={savingListing || listingLogoUploading} className="bg-slate-900 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700 disabled:bg-slate-300" data-testid="save-listing-btn">{savingListing ? 'Saving…' : 'Save changes'}</button>
                    <button onClick={() => saveListing({ listing_status: 'Approved', published: true })} disabled={savingListing || listingLogoUploading} className="bg-emerald-700 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-800 disabled:bg-slate-300" data-testid="approve-publish-listing-btn">Approve &amp; Publish</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {reviewVrp && vrpForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="vrp-review-modal">
            <div className="bg-slate-50 max-w-4xl w-full my-8">
              <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between sticky top-0 z-10">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{reviewVrp.legal_org_name || reviewVrp.trading_name || 'Application'}</h2>
                  <p className="text-xs text-slate-400 mt-1 font-mono">{reviewVrp.application_reference} · Submitted {reviewVrp.submitted_at ? new Date(reviewVrp.submitted_at).toLocaleDateString() : ''}</p>
                </div>
                <button onClick={() => { setReviewVrp(null); setVrpForm(null); }} className="text-slate-400 hover:text-slate-900 text-2xl leading-none" data-testid="vrp-modal-close">×</button>
              </div>

              <div className="p-6 space-y-6">
                {/* ADMIN CONTROLS */}
                <div className="bg-white border border-slate-200 p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Admin</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Status</label>
                      <select value={vrpForm.status} onChange={(e) => setVrpForm((p) => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="vrp-modal-status">
                        {(vrpStatuses.length ? vrpStatuses : ['New']).map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Assigned owner</label>
                      <input value={vrpForm.assigned_owner} onChange={(e) => setVrpForm((p) => ({ ...p, assigned_owner: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="vrp-modal-owner" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Next action</label>
                      <input value={vrpForm.next_action} onChange={(e) => setVrpForm((p) => ({ ...p, next_action: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="vrp-modal-next-action" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Next action date</label>
                      <input type="date" value={vrpForm.next_action_date} onChange={(e) => setVrpForm((p) => ({ ...p, next_action_date: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="vrp-modal-next-date" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tags (comma-separated)</label>
                      <input value={vrpForm.tags} onChange={(e) => setVrpForm((p) => ({ ...p, tags: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="vrp-modal-tags" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Internal notes <span className="text-slate-400 font-normal">(never shown publicly)</span></label>
                      <textarea rows={3} value={vrpForm.internal_notes} onChange={(e) => setVrpForm((p) => ({ ...p, internal_notes: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white" data-testid="vrp-modal-notes" />
                    </div>
                  </div>
                </div>

                {/* APPLICATION DETAIL */}
                {[
                  ['Contact', [['Name', `${reviewVrp.first_name || ''} ${reviewVrp.last_name || ''}`], ['Work email', reviewVrp.work_email], ['Phone', reviewVrp.phone], ['Job title', reviewVrp.job_title], ['Preferred contact', reviewVrp.preferred_contact_method]]],
                  ['Organisation', [['Legal name', reviewVrp.legal_org_name], ['Trading name', reviewVrp.trading_name], ['Website', reviewVrp.website], ['HQ country', reviewVrp.hq_country], ['Operating jurisdictions', reviewVrp.operating_jurisdictions], ['Type', reviewVrp.org_type], ['Size', reviewVrp.org_size]]],
                  ['AI Product', [['Product name', reviewVrp.product_name], ['Product URL', reviewVrp.product_url], ['Description', reviewVrp.product_description], ['Development stage', reviewVrp.development_stage], ['Direct interaction', reviewVrp.direct_interaction], ['Child audience', reviewVrp.child_audience], ['Age groups', (reviewVrp.age_groups || []).join(', ')], ['Estimated reach', reviewVrp.child_user_range], ['AI functionality', (reviewVrp.ai_functionality || []).join(', ')]]],
                  ['Current Position', [['Governance policy', reviewVrp.has_governance_policy], ['Safeguarding policy', reviewVrp.has_safeguarding_policy], ['Child risk assessment', reviewVrp.child_risk_assessment], ['Escalation documented', reviewVrp.escalation_documented], ['Documentation quantity', reviewVrp.documentation_quantity], ['Concerns / gaps', reviewVrp.current_concerns]]],
                  ['Objectives & Commercial', [['Trigger', reviewVrp.application_trigger], ['Success outcome', reviewVrp.success_outcome], ['Desired start', reviewVrp.desired_start_date], ['Specific deadline', reviewVrp.specific_deadline], ['Programme of interest', reviewVrp.programme_of_interest], ['Selected tier', reviewVrp.selected_tier], ['Indicative budget', reviewVrp.indicative_budget], ['Referral source', reviewVrp.referral_source], ['Additional info', reviewVrp.additional_information]]],
                  ['Consent', [['Authorised', reviewVrp.declaration_authorised ? 'Yes' : 'No'], ['Understands not verification', reviewVrp.declaration_not_verification ? 'Yes' : 'No'], ['Understands no guarantee', reviewVrp.declaration_no_guarantee ? 'Yes' : 'No'], ['Agreed to privacy', reviewVrp.declaration_privacy ? 'Yes' : 'No'], ['Marketing opt-in', reviewVrp.marketing_opt_in ? 'Yes' : 'No']]],
                ].map(([section, rows]) => (
                  <div key={section} className="bg-white border border-slate-200 p-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">{section}</p>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
                      {rows.filter(([, v]) => v && String(v).trim()).map(([label, value]) => (
                        <div key={label} className="flex flex-col">
                          <dt className="text-xs text-slate-400">{label}</dt>
                          <dd className="text-sm text-slate-800 break-words">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}

                <div className="flex justify-end gap-3">
                  <button onClick={() => { setReviewVrp(null); setVrpForm(null); }} className="bg-white border border-slate-300 text-slate-700 px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-100" data-testid="vrp-modal-cancel">Close</button>
                  <button onClick={saveVrp} disabled={savingVrp} className="bg-slate-900 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700 disabled:bg-slate-300" data-testid="vrp-modal-save">{savingVrp ? 'Saving…' : 'Save changes'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <p className="caption text-slate-500 mb-3">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value, multiline, link }) => {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="text-xs text-slate-500 uppercase tracking-wider block mb-1">{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline break-all">{value}</a>
      ) : (
        <span className={multiline ? 'block whitespace-pre-wrap text-slate-900 leading-relaxed' : 'text-slate-900'}>{value}</span>
      )}
    </div>
  );
};

const EditField = ({ label, name, value, onChange, multiline, type = 'text' }) => (
  <div className="text-sm">
    <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">{label}</label>
    {multiline ? (
      <textarea
        name={name}
        value={value || ''}
        onChange={onChange}
        rows={3}
        className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
        data-testid={`edit-field-${name}`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value || ''}
        onChange={onChange}
        className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
        data-testid={`edit-field-${name}`}
      />
    )}
  </div>
);

export default AdminPage;
